// Full report ("dossier") documents: one long-form Word/PDF report built from a saved report made from a template.
// Each template section becomes a chapter with its findings text (sentences carry numbered source notes), the
// section's own results table, optional centrality table and the figures the exporter added (graph, timeline,
// chart, money flows, clusters). Then the source notes with their evidence level, the evidence legend and the
// annexes (corrections, bibliography, glossary). One model feeds both renderers: DOCX in the app Worker
// (apps/app/src/exports/dossier-docx.ts) and the HTML page printed to PDF (dossier-html.ts). Off-record claims never
// appear; every page is watermarked like every other export.

import type { CentralityRow } from './centrality';
import type { ChatCaveat } from './chat';
import type { NoteLink } from './claim-links';
import type { AttributionLevel, ClaimKind, ReviewStatus } from './claims';
import { DOSSIER_LABELS, type DossierLabels, dossierLabels } from './dossier-labels';
import type { EvidenceLevel } from './evidence';
import { findingsBlocks, splitSentences, stripMarkers } from './findings';
import { type FlowItem, flowValue } from './flows';
import type { ExportFormat } from './profile-export';
import type { ReliabilityStatus } from './reliability';
import type { BibliographyEntry, ReportAnnexKey, ReportLayout } from './report-document';
import type { ReportLanguage } from './report-language';
import type { EntityKind } from './schema/types';
import { base64Bytes, pngSize } from './snapshots';

/** The findings section drafted last, from the other sections' text: it opens the document. */
export const EXECUTIVE_SUMMARY_KEY = 'executive_summary';
export const EXECUTIVE_SUMMARY_TITLE = DOSSIER_LABELS.en.executiveSummary;

export const DOSSIER_FIGURE_KINDS = [
  'graph',
  'timeline',
  'chart',
  'sankey',
  'clusters',
  'centrality',
  'crosscheck',
  'tiles',
  'schematic',
  'status_timeline',
  'votes',
  'before_after',
] as const;
export type DossierFigureKind = (typeof DOSSIER_FIGURE_KINDS)[number];
export const isDossierFigureKind = (v: unknown): v is DossierFigureKind => DOSSIER_FIGURE_KINDS.includes(v as DossierFigureKind);
export const DOSSIER_FIGURE_LABELS: Record<DossierFigureKind, string> = {
  graph: 'Graph',
  timeline: 'Timeline',
  chart: 'Chart',
  sankey: 'Money flows',
  clusters: 'Clusters',
  centrality: 'Betweenness',
  crosscheck: 'Interview cross-check',
  tiles: 'Key figures',
  schematic: 'Schematic',
  status_timeline: 'Timeline with status',
  votes: 'Vote matrix',
  before_after: 'Before / after',
};
/** The kinds whose data sits in the spec (typed by the analyst or computed by the app), not read from entities. */
export const TYPED_FIGURE_KINDS: readonly DossierFigureKind[] = ['tiles', 'schematic', 'status_timeline', 'before_after'];

export const DOSSIER_LIMITS = {
  /** Figures in one document, all sections together (the byte budget of their SVG is REPORT_FIGURE_BUDGET_BYTES). */
  figures: 40,
  /** Bytes of one PNG figure. */
  figureBytes: 3_000_000,
  /** Pixels of a figure side. */
  side: 6000,
  /** Rows of one section's results table when the person asks for all rows. */
  sectionRows: 200,
  /** Rows of one section's results table by default (D384): the rest stays in the report's CSV/Excel export. */
  shortRows: 25,
  /** Rows of the centrality table of a section. */
  centralityRows: 15,
  /** Rows of each annex. */
  annexRows: 500,
  /** Characters of a figure caption. */
  caption: 200,
} as const;

export const DOSSIER_FORMAT_LABELS: Record<ExportFormat, string> = { pdf: 'PDF', docx: 'Word (DOCX)' };

// ─── Figures sent with an export ────────────────────────────────────────────────────────────────────────────

/** A figure as the export request carries it, after checks: a real PNG within the limits. */
export interface DossierFigureInput {
  kind: DossierFigureKind;
  /** The template section it belongs to; null = after the executive summary, before the first section. */
  sectionKey: string | null;
  caption: string;
  png: Uint8Array;
  width: number;
  height: number;
}

/**
 * Figures sent with a full-report export: `[{ kind, sectionKey?, caption?, png: <base64> }]`. Only real PNGs within
 * the limits pass; each picture is the exporter's own view, embedded as an image and never read.
 */
export function parseDossierFigures(v: unknown): { ok: true; value: DossierFigureInput[] } | { ok: false; error: string } {
  if (v === undefined || v === null) return { ok: true, value: [] };
  if (!Array.isArray(v)) return { ok: false, error: 'Figures must be a list.' };
  if (v.length > DOSSIER_LIMITS.figures) return { ok: false, error: `Add at most ${DOSSIER_LIMITS.figures} figures.` };
  const out: DossierFigureInput[] = [];
  for (const f of v) {
    const o = (f && typeof f === 'object' ? f : {}) as Record<string, unknown>;
    const kind = o.kind as DossierFigureKind;
    if (!DOSSIER_FIGURE_KINDS.includes(kind)) return { ok: false, error: 'A figure must be one of the known figure kinds.' };
    const png = parsePng(o.png, `A ${DOSSIER_FIGURE_LABELS[kind].toLowerCase()} figure`);
    if (!png.ok) return png;
    const sectionKey = typeof o.sectionKey === 'string' && /^[a-z0-9_]{1,60}$/.test(o.sectionKey) ? o.sectionKey : null;
    const caption = typeof o.caption === 'string' ? o.caption.trim().replace(/\s+/g, ' ').slice(0, DOSSIER_LIMITS.caption) : '';
    out.push({ kind, sectionKey, caption: caption || DOSSIER_FIGURE_LABELS[kind], ...png.value });
  }
  return { ok: true, value: out };
}

/** A base64 PNG within the limits, with its size; `what` names the figure in an error ("A chart figure"). */
function parsePng(
  raw: unknown,
  what: string,
): { ok: true; value: { png: Uint8Array; width: number; height: number } } | { ok: false; error: string } {
  if (typeof raw !== 'string' || raw.length > (DOSSIER_LIMITS.figureBytes * 4) / 3 + 64)
    return { ok: false, error: `${what} is missing or larger than 3 MB.` };
  const png = base64Bytes(raw);
  const size = png && pngSize(png);
  if (!png || !size) return { ok: false, error: `${what} is not a PNG image.` };
  if (!size.width || !size.height || size.width > DOSSIER_LIMITS.side || size.height > DOSSIER_LIMITS.side)
    return { ok: false, error: `${what} is too large.` };
  return { ok: true, value: { png, ...size } };
}

/**
 * A saved figure as the export request names it: its id, and (older clients) the client's rasterization of its SVG.
 * The server draws every figure itself; a PNG sent is only used when it can't.
 */
export interface DossierSavedFigurePng {
  id: string;
  png: Uint8Array | null;
  width: number;
  height: number;
}

/** Whether the figures sent name saved figures (`[{ id, png }]`) rather than carrying their own kind and caption. */
export const namesSavedFigures = (v: unknown): boolean =>
  Array.isArray(v) && v.length > 0 && v.every(f => !!f && typeof f === 'object' && typeof (f as { id?: unknown }).id === 'string');

/**
 * The report's saved figures named by an export: `[{ id, png?: <base64> }]` (Report v2). The server matches each id
 * to the figure and keeps its section, caption and order; an id the report has no figure for is dropped. A PNG, when
 * sent, must be a real picture within the limits.
 */
export function parseSavedFigurePngs(v: unknown): { ok: true; value: DossierSavedFigurePng[] } | { ok: false; error: string } {
  if (v === undefined || v === null) return { ok: true, value: [] };
  if (!Array.isArray(v)) return { ok: false, error: 'Figures must be a list.' };
  if (v.length > DOSSIER_LIMITS.figures) return { ok: false, error: `Add at most ${DOSSIER_LIMITS.figures} figures.` };
  const out: DossierSavedFigurePng[] = [];
  for (const f of v) {
    const o = (f && typeof f === 'object' ? f : {}) as Record<string, unknown>;
    if (typeof o.id !== 'string' || !/^[0-9A-Za-z_-]{1,64}$/.test(o.id))
      return { ok: false, error: 'A figure must name one of the report’s figures.' };
    if (o.png === undefined || o.png === null || o.png === '') {
      out.push({ id: o.id, png: null, width: 0, height: 0 });
      continue;
    }
    const png = parsePng(o.png, 'A figure’s picture');
    if (!png.ok) return png;
    out.push({ id: o.id, ...png.value });
  }
  return { ok: true, value: out };
}

/** Per-section choices of the export request: `sections: [{ key, table?, centrality? }]`. */
export interface DossierSectionOptions {
  key: string;
  /** Include the section's results table (default true). */
  table: boolean;
  /** Print every row up to `DOSSIER_LIMITS.sectionRows` (default false: the first `shortRows`). */
  allRows: boolean;
  /** Include the "most central actors" table of the section's results (default false). */
  centrality: boolean;
  /** A field key: the results table is grouped by this field, one sub-heading and table per value (Report v2). */
  groupBy?: string | null;
  /** After the results table, the money links among the section's rows (Report v2). */
  flowsTable?: boolean;
}

// ─── The document model ─────────────────────────────────────────────────────────────────────────────────────

/** A run of text; `notes` are the numbers of the source notes that support it (printed after it). */
export interface DossierSegment {
  text: string;
  bold?: boolean;
  italic?: boolean;
  notes?: number[];
}

/** A paragraph (`p`) or a bullet item (`li`) of findings text. */
export interface DossierParagraph {
  kind: 'p' | 'li';
  segments: DossierSegment[];
}

export interface DossierTable {
  /** Table number across the whole document (1, 2, …), set by `buildDossierDocument`. */
  n?: number;
  /** What the table is: a section's results, its central actors or money flows, or the data behind a figure. */
  kind?: 'results' | 'centrality' | 'flows' | 'figure';
  title: string;
  headers: string[];
  rows: string[][];
  /** All rows that matched (rows may stop at a limit). */
  total: number;
  /** Shown under the table ("Showing the first 200 of 412 matches."), or null. */
  note: string | null;
  /**
   * A table grouped by a field: one sub-heading and table per value, in this order (the rows without a value last).
   * `rows` still holds every row; the renderers print the groups instead.
   */
  groups?: { title: string; rows: string[][] }[];
}

/**
 * A figure placed in the document; `image` is the index of its PNG in the images passed to a renderer, `svg` the
 * same picture as SVG (the PDF page embeds it inline; Word keeps it next to the PNG).
 */
export interface DossierFigure {
  /** Figure number across the whole document (1, 2, …). */
  n: number;
  kind: DossierFigureKind;
  caption: string;
  width: number;
  height: number;
  image: number;
  /** The print-palette SVG of the figure, when the server drew it (or holds the saved picture). */
  svg?: string | null;
  /** The number of the figure's data table ("data: table 4"), when it has one. */
  table?: number | null;
  /** The source line printed under the caption ("Source: EU Transparency Register, read 20 October 2026"). */
  source?: string | null;
}

/** The data behind a figure, printed as a numbered table right after it. */
export interface DossierFigureData {
  title: string;
  headers: string[];
  rows: string[][];
}

export interface DossierSection {
  key: string;
  title: string;
  paragraphs: DossierParagraph[];
  tables: DossierTable[];
  figures: DossierFigure[];
}

/** A numbered source note: one cited claim, as the findings cited it. */
export interface DossierNote {
  n: number;
  claimId: string;
  entityName: string;
  /** The claim in words ("Field: value"). */
  text: string;
  status: ReviewStatus;
  claimKind: ClaimKind;
  attributionLevel: AttributionLevel;
  evidence: EvidenceLevel;
  /** Official document / Corroborated / Single source / Hypothesis (`reliability.ts`). */
  reliability: ReliabilityStatus;
  /** The named person's response and contradicting claims (`claim-links.ts`), never off-record or rejected. */
  links?: NoteLink[];
  /** The source document or cited source, in words, or null. */
  source: string | null;
}

/** A claim review rejected or found disputed, for the corrections annex. */
export interface DossierCorrection {
  entityName: string;
  claim: string;
  status: 'rejected' | 'disputed';
  /** Why, when a reviewer or the checks said so. */
  reason: string | null;
  source: string | null;
}

/** A source the cited claims rest on, for the bibliography annex. */
export interface DossierSource {
  /** The document's or cited source's id, when known (the Sources block links to it). */
  id?: string;
  title: string;
  /** "Document", "Web page", "Interview", "Cited source"… */
  kind: string;
  /** Its own date (published, recorded); a web page without one: the day it was captured. */
  date: string | null;
  url: string | null;
  /** How many cited claims rest on it. */
  cited: number;
  /** The cited claims it backs, in citation order (the full report orders the annex by their first note). */
  claimIds?: string[];
}

export interface DossierGlossaryTerm {
  term: string;
  meaning: string;
}

export interface DossierAnnexes {
  corrections: DossierCorrection[];
  bibliography: DossierSource[];
  glossary: DossierGlossaryTerm[];
}

/** Which optional parts print: a block hidden in the report's layout is left out (Report v2). */
export interface DossierInclude {
  summary: boolean;
  /** The numbered source notes and the evidence legend. */
  sources: boolean;
  annexes: Record<ReportAnnexKey, boolean>;
}

export const DOSSIER_INCLUDE_ALL: DossierInclude = {
  summary: true,
  sources: true,
  annexes: { corrections: true, bibliography: true, glossary: true },
};

export interface DossierDocument {
  /** The language of the document's fixed labels (and of the findings text printed). */
  language: ReportLanguage;
  title: string;
  description: string | null;
  /** The report's owner, for the title page ("by …"); null when unknown. */
  author?: string | null;
  /** When the report was made (title page), or null. */
  createdAt?: number | null;
  /** What prints (everything when absent). */
  include?: DossierInclude;
  /** Annex B, after the cited sources: the entries the analyst added by hand. */
  bibliographyExtra?: { text: string; url: string | null }[];
  /** Executive summary paragraphs (empty when the findings have none). */
  summary: DossierParagraph[];
  /** Figures placed before the first section (sectionKey null), and their data tables. */
  leadFigures: DossierFigure[];
  leadTables?: DossierTable[];
  sections: DossierSection[];
  notes: DossierNote[];
  evidenceLegend: { level: EvidenceLevel; meaning: string }[];
  annexes: DossierAnnexes;
  /** "Before you rely on this report": the findings caveats, in words. */
  caveats: string[];
  status: 'no_findings' | 'draft' | 'signed_off';
  signedOffBy: string | null;
  withheld: string;
  exportId: string;
  exportedBy: string;
  exportedAt: number;
  watermark: string;
}

/** What `buildDossierDocument` needs, gathered by the export route. */
export interface DossierInput {
  /** The document's language: its fixed labels (default English). The findings given are already in it. */
  language?: ReportLanguage;
  report: { name: string; description: string | null; ownerName?: string | null; createdAt?: number | null };
  /** The template sections in order (the report's own copy); none = one section with the report's results. */
  sections: { key: string; title: string }[];
  /**
   * The report's layout (Report v2): the blocks print in its order and hidden ones are left out (a hidden section
   * takes its tables and figures with it). Without one, everything prints in template order.
   */
  layout?: ReportLayout | null;
  /** The bibliography entries the analyst added (Annex B, after the cited sources). */
  bibliographyExtra?: readonly BibliographyEntry[];
  /** Chapter titles to print instead of the template's, by section key (the findings' own titles, or translated ones). */
  sectionTitles?: Record<string, string>;
  /** The findings to print, or null when the report has none yet. */
  findings: {
    sections: { key: string; title: string; body: string }[];
    citations: {
      ref: string;
      claimId: string;
      entityName: string;
      text: string;
      status: ReviewStatus;
      claimKind: ClaimKind;
      attributionLevel: AttributionLevel;
    }[];
    status: 'draft' | 'signed_off' | 'choose' | 'empty';
    signedOffBy: string | null;
    caveats: ChatCaveat[];
  } | null;
  /** Evidence level and source of each cited claim, by claim id. */
  noteFacts: Record<string, { evidence: EvidenceLevel; reliability?: ReliabilityStatus; source: string | null; links?: NoteLink[] }>;
  /** Tables of each section, by section key (results table first, then centrality if asked). */
  tables: Record<string, DossierTable[]>;
  /** Figures in the order sent; `image` indexes are assigned in this order. */
  figures: {
    kind: DossierFigureKind;
    sectionKey: string | null;
    caption: string;
    width: number;
    height: number;
    svg?: string | null;
    source?: string | null;
    /** The figure's data, printed as its data table. */
    data?: DossierFigureData | null;
  }[];
  annexes: DossierAnnexes;
  evidenceLegend: { level: EvidenceLevel; meaning: string }[];
  exportId: string;
  exportedBy: string;
  exportedAt: number;
  watermark: string;
}

/**
 * Builds the document: findings text parsed into paragraphs with numbered notes (in order of first citation, one
 * number per claim), the executive summary out of the sections, figures numbered across the document and placed in
 * their sections, tables per section, caveats in words. Pure; both renderers take its output as is.
 */
/**
 * Annex B in the order the document first cites its sources: by the lowest note number among the claims each
 * backs. Sources whose claims have no note (cited only where the document doesn't print) keep their order, last.
 */
export function byFirstNote(sources: readonly DossierSource[], notes: readonly Pick<DossierNote, 'n' | 'claimId'>[]): DossierSource[] {
  const noteOf = new Map(notes.map(n => [n.claimId, n.n]));
  const first = (s: DossierSource) => Math.min(...(s.claimIds ?? []).map(id => noteOf.get(id) ?? Number.POSITIVE_INFINITY));
  return sources
    .map((s, i) => ({ s, i, n: first(s) }))
    .sort((a, b) => a.n - b.n || a.i - b.i)
    .map(x => x.s);
}

export function buildDossierDocument(input: DossierInput): DossierDocument {
  const language = input.language ?? 'en';
  const l = dossierLabels(language);
  const f = input.findings;
  const findingsSections = f?.sections ?? [];
  const titled = (key: string, title: string) => input.sectionTitles?.[key]?.trim() || title;
  const summarySource = findingsSections.find(s => s.key === EXECUTIVE_SUMMARY_KEY);

  // Chapters: every template section in layout order (even without text), hidden ones left out; none = one chapter
  // with the report's results. Findings sections no template section matches (findings drafted before the report
  // had a template) follow.
  const blocks = input.layout?.blocks ?? [];
  const isHidden = (kind: string, key: string) => blocks.some(b => b.kind === kind && b.key === key && b.hidden);
  const ordered = blocks.length
    ? [
        ...blocks.flatMap(b => (b.kind === 'section' ? input.sections.filter(s => s.key === b.key) : [])),
        ...input.sections.filter(s => !blocks.some(b => b.kind === 'section' && b.key === s.key)),
      ]
    : input.sections;
  const hiddenKeys = new Set(ordered.filter(s => isHidden('section', s.key)).map(s => s.key));
  const chapters: { key: string; title: string }[] = input.sections.length
    ? ordered.filter(s => !hiddenKeys.has(s.key)).map(s => ({ key: s.key, title: titled(s.key, s.title) }))
    : [{ key: 'results', title: titled('results', l.results) }];
  for (const s of findingsSections)
    if (s.key !== EXECUTIVE_SUMMARY_KEY && !hiddenKeys.has(s.key) && !chapters.some(x => x.key === s.key))
      chapters.push({ key: s.key, title: titled(s.key, s.title) });
  const include: DossierInclude = {
    summary: !isHidden('summary', 'summary'),
    sources: !isHidden('sources', 'sources'),
    annexes: {
      corrections: !isHidden('annex', 'corrections'),
      bibliography: !isHidden('annex', 'bibliography'),
      glossary: !isHidden('annex', 'glossary'),
    },
  };

  // Notes: one number per claim, in order of first citation across the document (summary first). Unknown refs drop.
  const byRef = new Map((f?.citations ?? []).map(c => [c.ref, c]));
  const numbers = new Map<string, number>();
  const notes: DossierNote[] = [];
  const noteOf = (ref: string): number | null => {
    const c = byRef.get(ref);
    if (!c) return null;
    const seen = numbers.get(c.claimId);
    if (seen) return seen;
    const n = notes.length + 1;
    numbers.set(c.claimId, n);
    const facts = input.noteFacts[c.claimId];
    notes.push({
      n,
      claimId: c.claimId,
      entityName: c.entityName,
      text: c.text,
      status: c.status,
      claimKind: c.claimKind,
      attributionLevel: c.attributionLevel,
      evidence: facts?.evidence ?? 'C',
      reliability: facts?.reliability ?? 'hypothesis',
      source: facts?.source ?? null,
      links: facts?.links ?? [],
    });
    return n;
  };

  const summary = summarySource && include.summary ? findingsParagraphs(summarySource.body, noteOf) : [];

  // Figures: unknown or no section → before the first chapter; a hidden section's figures are left out; numbered
  // lead figures first, then chapter by chapter.
  const keys = new Set(chapters.map(s => s.key));
  const placed = input.figures
    .map((fig, image) => ({ fig, image, at: fig.sectionKey && keys.has(fig.sectionKey) ? fig.sectionKey : null }))
    .filter(p => !(p.fig.sectionKey && hiddenKeys.has(p.fig.sectionKey)));
  // Figures and tables are numbered across the document in reading order: a figure's data table takes the next
  // table number right after it (so its caption can say "data: table N"), then the chapter's own tables.
  let figureN = 0;
  let tableN = 0;
  const figuresAt = (key: string | null): { figures: DossierFigure[]; tables: DossierTable[] } => {
    const figures: DossierFigure[] = [];
    const tables: DossierTable[] = [];
    for (const { fig, image } of placed.filter(p => p.at === key)) {
      const n = ++figureN;
      let table: number | null = null;
      if (fig.data) {
        table = ++tableN;
        tables.push({
          n: table,
          kind: 'figure',
          title: fig.data.title,
          headers: fig.data.headers,
          rows: fig.data.rows,
          total: fig.data.rows.length,
          note: null,
        });
      }
      figures.push({
        n,
        kind: fig.kind,
        caption: fig.caption,
        width: fig.width,
        height: fig.height,
        image,
        svg: fig.svg ?? null,
        table,
        source: fig.source ?? null,
      });
    }
    return { figures, tables };
  };
  const lead = figuresAt(null);
  const leadFigures = lead.figures;
  const leadTables = lead.tables;

  const sections: DossierSection[] = chapters.map(ch => {
    const body = findingsSections.find(s => s.key === ch.key)?.body ?? '';
    const own = figuresAt(ch.key);
    const tables = [...own.tables, ...(input.tables[ch.key] ?? []).map(t => ({ ...t, n: ++tableN, kind: t.kind ?? 'results' }))];
    return {
      key: ch.key,
      title: ch.title,
      paragraphs: findingsParagraphs(body, noteOf),
      tables,
      figures: own.figures,
    };
  });

  const written = !!f && f.status !== 'empty' && findingsSections.some(s => s.body.trim());
  const status: DossierDocument['status'] = !written ? 'no_findings' : f.status === 'signed_off' ? 'signed_off' : 'draft';

  return {
    language,
    title: input.report.name,
    description: input.report.description,
    author: input.report.ownerName ?? null,
    createdAt: input.report.createdAt ?? null,
    include,
    bibliographyExtra: (input.bibliographyExtra ?? []).map(e => ({ text: e.text, url: e.url ?? null })),
    summary,
    leadFigures,
    leadTables,
    sections,
    notes,
    evidenceLegend: input.evidenceLegend,
    annexes: { ...input.annexes, bibliography: byFirstNote(input.annexes.bibliography, notes) },
    caveats: (f?.caveats ?? []).map(c => c.text),
    status,
    signedOffBy: status === 'signed_off' ? (f?.signedOffBy ?? null) : null,
    withheld: l.withheld,
    exportId: input.exportId,
    exportedBy: input.exportedBy,
    exportedAt: input.exportedAt,
    watermark: input.watermark,
  };
}

/** `**bold**` and `*italic*` (or `_italic_`), as in the findings editor (findings-text.tsx). */
const INLINE = /\*\*([^*]+)\*\*|\*([^*\s][^*]*)\*|_([^_\s][^_]*)_/g;

function inlineSegments(text: string): DossierSegment[] {
  const out: DossierSegment[] = [];
  let last = 0;
  for (const m of text.matchAll(INLINE)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ text: text.slice(last, i) });
    if (m[1] !== undefined) out.push({ text: m[1], bold: true });
    else out.push({ text: (m[2] ?? m[3]) as string, italic: true });
    last = i + m[0].length;
  }
  if (last < text.length) out.push({ text: text.slice(last) });
  return out;
}

/**
 * One findings section's text as paragraphs and bullet items: blocks and sentences split exactly as the findings
 * editor and its checks split them (findingsBlocks, splitSentences); each sentence's markers become the notes of its
 * last run of text and leave the text.
 */
function findingsParagraphs(body: string, noteOf: (ref: string) => number | null): DossierParagraph[] {
  const out: DossierParagraph[] = [];
  for (const block of findingsBlocks(body)) {
    const segments: DossierSegment[] = [];
    for (const sentence of splitSentences(block.text)) {
      const text = stripMarkers(sentence.raw);
      if (!text) continue;
      const parts = inlineSegments(text);
      const notes: number[] = [];
      for (const r of sentence.refs) {
        const n = noteOf(r);
        if (n !== null && !notes.includes(n)) notes.push(n);
      }
      const end = parts.at(-1);
      if (end && notes.length) end.notes = notes;
      if (segments.length) segments.push({ text: ' ' });
      segments.push(...parts);
    }
    if (segments.length) out.push({ kind: block.kind, segments: mergeSegments(segments) });
  }
  return out;
}

/** Adjacent runs with the same emphasis and no notes in between become one. */
function mergeSegments(segments: DossierSegment[]): DossierSegment[] {
  const out: DossierSegment[] = [];
  for (const s of segments) {
    const prev = out.at(-1);
    if (prev && !prev.notes && !!prev.bold === !!s.bold && !!prev.italic === !!s.italic) {
      prev.text += s.text;
      if (s.notes) prev.notes = s.notes;
    } else out.push({ ...s });
  }
  return out;
}

/** The "most central actors" table of a section, from centrality rows, labelled in the document's language. */
export function centralityTable(rows: readonly CentralityRow[], total: number, lang: ReportLanguage = 'en'): DossierTable {
  const l = dossierLabels(lang);
  const shown = rows.slice(0, DOSSIER_LIMITS.centralityRows);
  const all = Math.max(total, rows.length);
  return {
    kind: 'centrality',
    title: l.centralityTitle,
    headers: [...l.centralityHeaders],
    rows: shown.map(r => [String(r.rank), r.name, String(r.degree), r.betweenness.toFixed(2)]),
    total: all,
    note: all > shown.length ? l.centralityNote(shown.length, all) : null,
  };
}

/** "Figure 3. Money by camp (data: table 4)". */
export const dossierFigureCaption = (f: Pick<DossierFigure, 'n' | 'caption' | 'table'>, lang: ReportLanguage = 'en') => {
  const l = dossierLabels(lang);
  return `${l.figure(f.n)} ${f.caption}${f.table ? ` (${l.dataTable(f.table)})` : ''}`;
};

/** "Table 4. Actors by level" (the title alone when the table has no number). */
export const dossierTableCaption = (t: Pick<DossierTable, 'n' | 'title'>, lang: ReportLanguage = 'en') =>
  t.n ? `${dossierLabels(lang).table(t.n)} ${t.title}` : t.title;

/** The source line under a figure: "Source: …". */
export const dossierFigureSource = (f: Pick<DossierFigure, 'source'>, lang: ReportLanguage = 'en') =>
  f.source ? `${dossierLabels(lang).source} ${f.source}` : null;

/** The title page's meta line: "by Owner · created 15 October 2026 · language English" (what is known). */
export function dossierMetaLine(doc: Pick<DossierDocument, 'language' | 'author' | 'createdAt'>): string {
  const l = dossierLabels(doc.language);
  return [
    doc.author ? l.by(doc.author) : null,
    doc.createdAt ? l.created(formatDossierDate(doc.createdAt, doc.language)) : null,
    `${l.language} ${l.languageNames[doc.language]}`,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** A date in words in the document's language, day first ("15 October 2026", "15 octobre 2026"), UTC. */
export const formatDossierDate = (ms: number, lang: ReportLanguage) =>
  new Intl.DateTimeFormat(lang === 'fr' ? 'fr-FR' : 'en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(ms),
  );

/** The note under a section table limited to the report's question (D395), in the document's language. */
export const withinNote = (kind: EntityKind, withinKind: EntityKind, words: string, l: DossierLabels) =>
  kind === withinKind ? l.withinSame(words) : l.withinLinked(kind, words);

/**
 * A results table grouped by one of its columns: one group per distinct value, in `order` when given (the field's
 * list order), else as the values first appear; the rows with an empty value last, under "Not set".
 */
export function groupTable(t: DossierTable, column: number, field: string, l: DossierLabels, order: readonly string[] = []): DossierTable {
  const by = new Map<string, string[][]>();
  for (const r of t.rows) {
    const v = (r[column] ?? '').trim();
    by.set(v, [...(by.get(v) ?? []), r]);
  }
  const seen = [...by.keys()].filter(Boolean);
  const rank = (v: string) => {
    const i = order.indexOf(v);
    return i === -1 ? order.length + seen.indexOf(v) : i;
  };
  const values = [...seen].sort((a, b) => rank(a) - rank(b));
  const groups = values.map(v => ({ title: l.group(field, v), rows: by.get(v) as string[][] }));
  const empty = by.get('');
  if (empty?.length) groups.push({ title: l.group(field, l.notSet), rows: empty });
  return { ...t, groups };
}

/** Rows of a flows table in one document. */
export const DOSSIER_FLOW_ROWS = 100;

/**
 * The money links among a section's rows as a table: payer → recipient, amount, currency, period, source note;
 * largest first (undisclosed last), at most DOSSIER_FLOW_ROWS. `names` by entity id, `sources` by claim id.
 */
export function flowsTable(
  flows: readonly FlowItem[],
  names: ReadonlyMap<string, string>,
  sources: ReadonlyMap<string, string | null>,
  l: DossierLabels,
): DossierTable {
  const sorted = [...flows].sort((a, b) => (flowValue(b) ?? -1) - (flowValue(a) ?? -1) || a.id.localeCompare(b.id));
  const shown = sorted.slice(0, DOSSIER_FLOW_ROWS);
  // The amount as written, else the number alone (the currency has its own column); undisclosed when none.
  const amount = (f: FlowItem) => {
    const v = flowValue(f);
    return f.amountText?.trim() || (v === null ? l.undisclosed : v.toLocaleString(l.locale, { maximumFractionDigits: v < 100 ? 2 : 0 }));
  };
  const period = (f: FlowItem) =>
    f.periodText?.trim() ||
    (f.periodFrom && f.periodFrom === f.periodTo ? f.periodFrom : [f.periodFrom, f.openEnd ? '…' : f.periodTo].filter(Boolean).join(' – '));
  return {
    kind: 'flows',
    title: l.flowsTitle,
    headers: [...l.flowsHeaders],
    rows: shown.map(f => [
      names.get(f.from) ?? f.from,
      names.get(f.to) ?? f.to,
      amount(f),
      f.currency ?? '',
      period(f),
      sources.get(f.claimId) ?? '',
    ]),
    total: flows.length,
    note: flows.length > shown.length ? l.flowsNote(shown.length, flows.length) : null,
  };
}
